import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

export interface ClientInvitationRow {
  id: string;
  trainer_id: string;
  email: string | null;
  phone: string | null;
  invite_code: string | null;
  invite_type: string | null;
  status: string | null;
  client_name: string | null;
  initial_profile: {
    fitness_level?: string | null;
    primary_goal?: string | null;
    workout_frequency?: number | null;
    session_duration?: number | null;
    available_equipment?: string[] | null;
  } | null;
  created_at: string;
  expires_at: string | null;
  accepted_at: string | null;
  accepted_by: string | null;
}

function generateInviteCode(): string {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

export function useInvitations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['invitations', user?.id],
    queryFn: async (): Promise<ClientInvitationRow[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('client_invitations')
        .select('*')
        .eq('trainer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as ClientInvitationRow[]) ?? [];
    },
    enabled: !!user?.id,
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('client_invitations')
        .update({ status: 'revoked' })
        .eq('id', id)
        .eq('trainer_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useRegenerateInvitationCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');

      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      let lastError: unknown = null;

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const inviteCode = generateInviteCode();
        const { data, error } = await supabase
          .from('client_invitations')
          .update({
            invite_code: inviteCode,
            expires_at: expiresAt,
            accepted_at: null,
            accepted_by: null,
          })
          .eq('id', id)
          .eq('trainer_id', user.id)
          .eq('status', 'pending')
          .select('id');

        if (!error) {
          // Accepted and revoked invitations stay closed; the list may predate the change.
          if (data.length === 0) throw new Error('This invitation is no longer pending.');
          return inviteCode;
        }
        lastError = error;
      }

      throw lastError instanceof Error ? lastError : new Error('Failed to regenerate code');
    },
    // Settled, not success: a Resend that finds the invitation no longer pending must refresh the list too.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invitations'] }),
  });
}
